// Balance (SM-754): top-up, manual credit and manual deduct with a
// mandatory reason. Amounts are validated to ≤ 4 dp client-side; a
// manual deduct needs the typed phrase "DEDUCT <amount 4dp> <user>". The
// answer's before → after is shown verbatim, with the adjustment history
// and the audit rows the action wrote.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AdjustmentsTable } from "@/components/domain/AdjustmentsTable";
import { AuditTable } from "@/components/domain/AuditTable";
import { UserPicker } from "@/components/domain/UserPicker";
import { StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Field, TextInput } from "@/components/ui/Field";
import { Empty, KV, Mono, PageHeader, ReadOnlyHint } from "@/components/ui/Misc";
import { checkAmount } from "@/lib/amount";
import { api, newRequestId } from "@/lib/api";
import { deductPhrase } from "@/lib/confirm";
import { fmtTime } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";
import { useCanMutate } from "@/lib/session";
import type { Adjustment, AdjustKind } from "@/lib/types";

interface BalanceSearch {
  user?: string;
}

export const Route = createFileRoute("/_app/balance")({
  validateSearch: (s: Record<string, unknown>): BalanceSearch => ({
    user: typeof s.user === "string" && s.user ? s.user : undefined,
  }),
  component: BalancePage,
});

const KINDS: Array<{ kind: AdjustKind; label: string; help: string }> = [
  { kind: "TOP_UP", label: "Top-up", help: "Adds funds — the everyday way to fund a test wallet." },
  { kind: "MANUAL_CREDIT", label: "Manual credit", help: "Adds funds as an explicit operator correction." },
  {
    kind: "MANUAL_DEDUCT",
    label: "Manual deduct",
    help: "Removes funds. Refused if it would make the wallet negative; nothing moves then.",
  },
];

function BalancePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/balance" });
  const userId = search.user ?? "";
  return (
    <div className="space-y-5">
      <PageHeader
        title="Balance"
        subtitle="Operator adjustments take the same per-user row lock as the wallet API, so they serialize with provider calls — none is lost or duplicated."
      />
      <div className="card">
        <UserPicker value={userId} onChange={(id) => navigate({ search: { user: id || undefined } })} />
      </div>
      {userId ? <BalanceForUser key={userId} userId={userId} /> : <Empty>Choose a user to adjust their wallet.</Empty>}
    </div>
  );
}

function BalanceForUser({ userId }: { userId: string }) {
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const user = useQuery({ queryKey: keys.user(userId), queryFn: () => fetchers.user(userId) });
  const adjustments = useQuery({ queryKey: keys.adjustments(userId), queryFn: () => fetchers.adjustments(userId) });
  const auditParams = { subject_kind: "user", subject_id: userId, limit: "10" };
  const audit = useQuery({ queryKey: keys.audit(auditParams), queryFn: () => fetchers.audit(auditParams) });

  const [kind, setKind] = useState<AdjustKind>("TOP_UP");
  const [amountRaw, setAmountRaw] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<Adjustment | null>(null);

  const amount = checkAmount(amountRaw);
  const reasonOk = reason.trim().length >= 3;
  const phrase = kind === "MANUAL_DEDUCT" ? deductPhrase(amountRaw, userId) : null;

  const send = async (confirm: string) => {
    if (!amount.ok) return;
    const adj = await api<Adjustment>(`/users/${encodeURIComponent(userId)}/balance`, {
      method: "POST",
      headers: { "X-Request-ID": newRequestId() },
      body: { kind, amount: amount.value, reason: reason.trim(), confirm },
    });
    setResult(adj);
    setAmountRaw("");
    await Promise.all([
      qc.invalidateQueries({ queryKey: keys.user(userId) }),
      qc.invalidateQueries({ queryKey: keys.adjustments(userId) }),
      qc.invalidateQueries({ queryKey: ["mock", "audit"] }),
      qc.invalidateQueries({ queryKey: ["mock", "users"] }),
    ]);
  };

  return (
    <>
      <ErrorBanner error={user.error} />
      {user.data && (
        <div className="card">
          <KV
            items={[
              [
                "User",
                <Link to="/users/$id" params={{ id: userId }} className="mono text-t-1 hover:underline">
                  {userId}
                </Link>,
              ],
              ["Name", user.data.display_name || "—"],
              ["Status", <StatusBadge status={user.data.account_status} />],
              ["Available balance", <span className="mono text-xl font-bold">{user.data.available_balance}</span>],
            ]}
          />
        </div>
      )}

      <SectionCard title="Adjust balance">
        {!canMutate && <ReadOnlyHint />}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!amount.ok || !reasonOk) return;
            if (kind === "MANUAL_DEDUCT") {
              setConfirmOpen(true);
              return;
            }
            setBusy(true);
            setError(null);
            try {
              await send("");
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <fieldset disabled={!canMutate || busy}>
            <div className="seg mb-2" role="radiogroup" aria-label="Adjustment kind">
              {KINDS.map((k) => (
                <button
                  key={k.kind}
                  type="button"
                  role="radio"
                  aria-checked={kind === k.kind}
                  className={kind === k.kind ? "is-active" : undefined}
                  onClick={() => setKind(k.kind)}
                  disabled={!canMutate}
                >
                  {k.label}
                </button>
              ))}
            </div>
            <p className="mb-4 text-xs text-t-3">{KINDS.find((k) => k.kind === kind)?.help}</p>
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
              <Field
                label="Amount"
                hint={amount.ok ? `Will send ${amount.value}` : "0.0001 – 92233720368.5477, at most 4 decimals"}
                error={amountRaw && !amount.ok ? amount.error : null}
              >
                <TextInput value={amountRaw} onChange={(e) => setAmountRaw(e.target.value)} inputMode="decimal" className="mono" placeholder="100.0000" />
              </Field>
              <Field label="Reason (required)" error={reason && !reasonOk ? "At least 3 characters." : null}>
                <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="why this wallet changes" />
              </Field>
            </div>
            <ErrorBanner error={error} className="mt-3" />
            <div className="mt-4 flex justify-end">
              <Button type="submit" variant={kind === "MANUAL_DEDUCT" ? "danger" : "primary"} loading={busy} disabled={!amount.ok || !reasonOk}>
                {KINDS.find((k) => k.kind === kind)?.label}
              </Button>
            </div>
          </fieldset>
        </form>
      </SectionCard>

      {result && (
        <div className="card border-2" style={{ borderColor: "var(--yes)" }} data-testid="adjust-result">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="pill yes">Applied</span>
            <Mono>{result.kind}</Mono>
            <span className="text-xs t-mute">{fmtTime(result.at)}</span>
          </div>
          <div className="flex flex-wrap items-baseline gap-3 font-mono text-xl font-bold text-t-1">
            <span>{result.balance_before}</span>
            <span className="t-mute">→</span>
            <span>{result.balance_after}</span>
            <span className={`text-sm ${result.kind === "MANUAL_DEDUCT" ? "t-no" : "t-yes"}`}>
              ({result.kind === "MANUAL_DEDUCT" ? "−" : "+"}
              {result.amount})
            </span>
          </div>
          <div className="mt-3">
            <KV
              items={[
                ["Adjustment id", <Mono>{result.adjustment_id}</Mono>],
                ["Request id", <Mono>{result.request_id}</Mono>],
                ["Operator", result.operator],
                ["Reason", result.reason],
              ]}
            />
          </div>
        </div>
      )}

      <SectionCard title="Adjustment history">
        <ErrorBanner error={adjustments.error} />
        <AdjustmentsTable rows={adjustments.data ?? []} />
      </SectionCard>

      <SectionCard title="Recent audit for this user">
        <ErrorBanner error={audit.error} />
        <AuditTable rows={audit.data ?? []} emptyText="No audit events for this user yet." />
      </SectionCard>

      <ConfirmDialog
        open={confirmOpen}
        title="Manually deduct from this wallet?"
        consequence={
          <>
            Removes <b className="mono">{amount.ok ? amount.value : "—"}</b> from <b className="mono">{userId}</b>
            {user.data ? (
              <>
                {" "}
                (available now <span className="mono">{user.data.available_balance}</span>)
              </>
            ) : null}
            . It is refused, and nothing moves, if the wallet would go negative. The reason is written to the append-only
            audit log.
          </>
        }
        phrase={phrase}
        confirmLabel="Deduct"
        onConfirm={send}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
